<template>
  <div>
    <h3>Enrichment Progress Overview
      <v-btn
        @click="$apollo.queries.allProductMatchingsOverview.refetch()"
        size="x-small"
        icon
        :loading="$apollo.queries.allProductMatchingsOverview?.loading"
      >
        <v-icon size="x-small">mdi-restore</v-icon>
      </v-btn>

    </h3>
    <br>
    <div v-if="$apollo.queries.allProductMatchingsOverview?.loading">
      <!-- Loading overview... -->
    </div>
    <v-row v-else>
      <v-col
        cols="4"
        v-for="participant in allProductMatchingsOverview"
        :key="participant.Participant_SK"
      >
        <div class="d-flex align-center mb-1">
          <h5 class="text-orange">{{ participant.Participant_Name }}</h5>
          <v-btn
            v-if="!isLocked"
            size="x-small"
            :variant="getParticipantButtonVariant(participant.Participant_SK)"
            :color="getParticipantButtonColor(participant.Participant_SK)"
            class="ml-3"
            @click="selectParticipant(participant.Participant_SK)"
          >
            {{ getParticipantButtonText(participant.Participant_SK) }}
          </v-btn>
        </div>
        <!-- Matrix table showing status counts -->
        <v-data-table
          class="customMatrixTable"
          :headers="tableHeaders"
          :items="buildTableData(participant.statusCounts, participant.notMatchedBrandBreakdown, participant.Participant_SK)"
          density="compact"
          hide-default-footer
          :items-per-page="-1"
          :sort-by="sortBy"
          :header-props="headerProps"
          mobile-breakpoint="0"
          :expanded="expandedRows[participant.Participant_SK] || []"
          show-expand
          expand-on-click
          @update:expanded="(val) => onExpandedChange(participant.Participant_SK, val)"
        >
          <template v-slot:item="{ item, isExpanded, toggleExpand }">
            <tr :class="{ 'expandable-row': item.status === 'Not Matched' && item.brandBreakdown?.length > 0 }">
              <!-- Expand toggle for Not Matched only -->
              <td style="width: 30px;">
                <v-btn
                  v-if="item.status === 'Not Matched' && item.brandBreakdown?.length > 0"
                  icon
                  variant="text"
                  size="x-small"
                  @click="toggleExpand(item)"
                >
                  <v-icon>{{ isExpanded(item) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </v-btn>
              </td>
              <!-- Status column  -->
              <td
                style="text-align: right; font-size: 12px;"
                class="font-weight-bold"
              >{{ item.status }}</td>

              <!-- Count column (right aligned, bold, blank if 0) -->
              <td
                style="text-align: right;"
                :class="['font-weight-bold', { 'not-matched-count': item.status === 'Not Matched' }]"
              >
                {{ item.count === 0 ? '' : new Intl.NumberFormat('en-US').format(item.count) }}
              </td>
            </tr>
          </template>

          <!-- Expanded row content for Not Matched brand breakdown -->
          <template v-slot:expanded-row="{ columns, item }">
            <tr class="expanded-content">
              <td :colspan="columns.length" class="pa-0">
                <v-table density="compact" class="brand-breakdown-table">
                  <tbody>
                    <tr
                      v-for="brand in item.brandBreakdown"
                      :key="brand.Brand_SK ?? 'no-brand'"
                    >
                      <td class="pl-8" style="font-size: 11px;">
                        <span :class="{ 'no-brand-name': !brand.Brand_SK }">{{ brand.Brand_Name }}</span>
                      </td>
                      <td style="text-align: right;" class="not-matched-count">
                        {{ new Intl.NumberFormat('en-US').format(brand.count) }}
                      </td>
                      <td style="width: 90px; text-align: right;">
                        <v-btn
                          v-if="!isLocked"
                          size="x-small"
                          :variant="getBrandButtonVariant(brand.Brand_SK)"
                          :color="getBrandButtonColor(brand.Brand_SK)"
                          @click="onBrandSelect(brand, item.participantSk)"
                        >
                          {{ getBrandButtonText(brand.Brand_SK) }}
                        </v-btn>
                      </td>
                    </tr>
                  </tbody>
                </v-table>
              </td>
            </tr>
          </template>

          <template v-slot:header.count>
            <span>COUNT</span>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { inject } from 'vue'
import { mapState } from 'vuex'
import ALL_PRODUCT_MATCHINGS_OVERVIEW from '@/graphql/Product/allProductMatchingsOverview.gql'

export default {
  name: 'ProductMatchingEnrichmentOverview',
  setup: () => ({ rc: inject('rc') }),
  emits: ['select-participant', 'select-brand', 'add-brand-to-selection', 'select-no-brand'],
  data () {
    return {
      allProductMatchingsOverview: [],
      headerProps: {
        style: 'font-size: 12px; padding: 1px;'
      },
      sortBy: [{ key: 'status', order: 'desc' }],
      expandedRows: {}  // Track expanded state per participant
    }
  },
  computed: {
    ...mapState(['selectionConfig']),
    isAuthenticated () {
      return this.$auth0.isAuthenticated.value
    },
    isLocked() {
      return this.selectionConfig?.isLocked || false
    },
    selectedBrandSks() {
      return this.selectionConfig?.brandSks || []
    },
    selectedParticipantSk() {
      return this.selectionConfig?.participantSk || null
    },
    tableHeaders() {
      return [
        {
          title: '',
          key: 'data-table-expand',
          width: '30px'
        },
        {
          title: '',
          key: 'status',
          align: 'center',
          divider: true,
        },
        {
          title: 'COUNT',
          key: 'count',
          align: 'end',
          divider: true,
        }
      ]
    }
  },
  methods: {
    buildTableData(statusCounts, brandBreakdown, participantSk) {
      return statusCounts.map(({ status, count }) => ({
        status,
        count,
        brandBreakdown: status === 'Not Matched' ? brandBreakdown : [],
        participantSk
      }))
    },
    onExpandedChange(participantSk, val) {
      this.expandedRows[participantSk] = val
    },
    selectParticipant(participantSk) {
      this.$emit('select-participant', { participantSk })
    },
    getParticipantButtonText(participantSk) {
      if (this.selectedParticipantSk === participantSk) return 'Selected'
      return 'Select'
    },
    getParticipantButtonVariant(participantSk) {
      if (this.selectedParticipantSk === participantSk) return 'flat'
      return 'outlined'
    },
    getParticipantButtonColor(participantSk) {
      if (this.selectedParticipantSk === participantSk) return 'success'
      return '#E65100'
    },
    getBrandButtonText(brandSk) {
      if (!brandSk) return 'Select'  // No Brand always "Select"
      if (this.selectedBrandSks.includes(brandSk)) return 'Selected'
      if (this.selectedBrandSks.length > 0) return '+ Add'
      return 'Select'
    },
    getBrandButtonVariant(brandSk) {
      if (this.selectedBrandSks.includes(brandSk)) return 'flat'
      return 'outlined'
    },
    getBrandButtonColor(brandSk) {
      if (this.selectedBrandSks.includes(brandSk)) return 'success'
      return '#E65100'
    },
    onBrandSelect(brand, participantSk) {
      if (!brand.Brand_SK) {
        // "No Brand" selection - row 5
        this.$emit('select-no-brand', { participantSk })
      } else if (this.selectedBrandSks.includes(brand.Brand_SK)) {
        // Already selected, do nothing or could toggle off
        return
      } else if (this.selectedBrandSks.length > 0) {
        // Add to existing selection
        this.$emit('add-brand-to-selection', { brandSk: brand.Brand_SK })
      } else {
        // New selection
        this.$emit('select-brand', { brandSk: brand.Brand_SK })
      }
    }
  },
  apollo: {
    allProductMatchingsOverview: {
      query: ALL_PRODUCT_MATCHINGS_OVERVIEW,
      skip () {
        return !this.isAuthenticated || !this.rc.allParticipants.length
      }
    }
  }
}

</script>

<style scoped>
.customMatrixTable :deep(.v-data-table__wrapper) td {
  border-right: 1px solid #444444;
}
.not-matched-count {
  color: #E65100;
}
.expandable-row {
  cursor: pointer;
}
.expanded-content {
  background-color: rgba(0, 0, 0, 0.02);
}
.brand-breakdown-table {
  background-color: transparent;
}
.no-brand-name {
  font-style: italic;
}
</style>
